'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import { ComponentSharedSlider } from '@peerless/data-access/strapi';
import { Autoplay, EffectFade, Navigation, Pagination } from 'swiper/modules';
import {
  alpha,
  Box,
  Button,
  Card,
  CardMedia,
  Container,
  IconButton,
  Stack,
  styled,
} from '@mui/material';
import {
  formatMediaUrl,
  formatPosition,
  formatSectionWidth,
  getColorConversion,
  isValidUrl,
} from '../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icons } from '@peerless/ui-assets/icons';
import { RouterLink } from '@/navigation';
import { useTranslations } from 'next-intl';
import { useId, useMemo } from 'react';
import { AutoplayOptions, PaginationOptions } from 'swiper/types';
import { withSectionLayout } from './SectionLayout';
import { SectionHeader } from './SectionHeader';

import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

interface SliderProps {
  data: ComponentSharedSlider;
}

const MIN_HEIGHT_DEFAULT = 300;
const TRANSITION_SPEED_DEFAULT = 500;

export default function _DynamicComponentSlider({ data }: SliderProps) {
  const t = useTranslations();
  const {
    minHeight,
    autoplay,
    autoplaySpeed,
    navigation,
    infiniteLoop,
    transition,
    transitionSpeed,
  } = data;
  const id = useId();
  const autoplayOptions = useMemo(() => {
    if (!autoplay) return undefined;
    const speed = autoplaySpeed > 0 ? (11 - autoplaySpeed) * 1000 : 3000;
    return { delay: speed } as AutoplayOptions;
  }, [autoplay, autoplaySpeed]);

  const paginationOptions = useMemo(() => {
    if (['Dots_and_Arrows', 'Dots'].includes(navigation)) {
      return {
        el: '.swiper-pagination',
        clickable: true,
      } as PaginationOptions;
    }
    return undefined;
  }, [navigation]);

  const navigationOptions = useMemo(() => {
    if (['Dots_and_Arrows', 'Arrows'].includes(navigation))
      return {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      };
    return undefined;
  }, [navigation]);

  return (
    <>
      <SectionHeader {...data.sectionHeader} />
      <ImageSwiper
        // install Swiper modules
        modules={[Navigation, Pagination, Autoplay, EffectFade]}
        spaceBetween={0}
        slidesPerView={1}
        navigation={navigationOptions}
        autoplay={autoplayOptions}
        loop={infiniteLoop}
        pagination={paginationOptions}
        speed={transitionSpeed ?? TRANSITION_SPEED_DEFAULT}
        effect={transition.toLowerCase()}
        sx={{ minHeight: minHeight ?? MIN_HEIGHT_DEFAULT }}
      >
        {data.sliderItems.map((item, i) => {
          const backgroundImage = item.backgroundImage.data?.attributes
            ? `url(${formatMediaUrl(item.backgroundImage.data.attributes)})`
            : undefined;
          return (
            <SwiperSlide key={`${id}-${i}`}>
              <Box
                sx={{
                  width: '100%',
                  backgroundImage: backgroundImage,
                  backgroundColor: !backgroundImage
                    ? getColorConversion(item.backgroundColor)
                    : undefined,
                  minHeight: minHeight ?? MIN_HEIGHT_DEFAULT,
                  backgroundSize: 'cover',
                  backgroundPosition: 'bottom, center',
                  backgroundRepeat: 'no-repeat',
                  transition: 'background-image 0.5s ease-in-out',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Container
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: '1',
                    justifyContent: formatPosition(item.contentVerticalPosition),
                    alignItems: formatPosition(item.contentHorizontalPosition),
                  }}
                >
                  <Box maxWidth={formatSectionWidth(item.contentWidth)} width={'100%'}>
                    {item.text && (
                      <Box sx={{ color: getColorConversion(item.textColor) }}>
                        <div dangerouslySetInnerHTML={{ __html: item.text }} />
                      </Box>
                    )}
                    {item.buttonLink && (
                      <Stack
                        flexDirection={'row'}
                        alignItems={formatPosition(item.contentVerticalPosition)}
                        justifyContent={formatPosition(item.contentHorizontalPosition)}
                      >
                        <Button
                          sx={{
                            color: getColorConversion(item.buttonColor),
                            borderColor: getColorConversion(item.buttonColor),
                            minWidth: '15rem',
                          }}
                          variant="outlined"
                          href={item.buttonLink}
                          component={RouterLink}
                        >
                          {item.buttonText ?? t('Search.link-here' as never)}
                        </Button>
                      </Stack>
                    )}
                  </Box>
                </Container>
              </Box>
            </SwiperSlide>
          );
        })}
        {data.imageSliderItems.map((item, i) => {
          const image = item.image.data?.attributes
            ? `url(${formatMediaUrl(item.image.data.attributes)})`
            : undefined;
          const target = isValidUrl(item.url) ? '_blank' : '_self';
          return (
            <SwiperSlide key={`${id}-${i}`}>
              <Button
                sx={{
                  padding: 0,
                }}
                href={item.url}
                target={target}
                component={item.url ? RouterLink : Box}
                className="slider-img-link"
              >
                {image && (
                  <Card sx={{}}>
                    <CardMedia
                      component={'img'}
                      alt={item.image.data.attributes.alternativeText}
                      image={formatMediaUrl(item.image.data.attributes)}
                    />
                  </Card>
                )}
              </Button>
            </SwiperSlide>
          );
        })}
        {navigationOptions && (
          <>
            <IconButton className="swiper-button swiper-button-next">
              <FontAwesomeIcon fixedWidth icon={icons.chevronRight} />
            </IconButton>
            <IconButton className="swiper-button swiper-button-prev">
              <FontAwesomeIcon fixedWidth icon={icons.chevronLeft} />
            </IconButton>
          </>
        )}

        <div className="swiper-pagination"></div>
      </ImageSwiper>
    </>
  );
}

const ImageSwiper = styled(Swiper)(({ theme }) => ({
  flex: 1,
  marginTop: 0,
  marginBottom: 0,
  width: '100%',

  '& .swiper-button': {
    position: 'absolute',
    width: 50,
    height: 50,
    marginTop: -25,
    backgroundColor: alpha(theme.palette.common.white, 0.25),
    display: 'flex',

    ':hover': {
      backgroundColor: theme.palette.common.white,
    },

    ':after': {
      content: '""',
    },

    '&.swiper-button-next': {
      left: 'auto',
      right: theme.spacing(1),
    },

    '&.swiper-button-prev': {
      left: theme.spacing(1),
      right: 'auto',
    },
  },

  '& .swiper-slide': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',

    '& .slider-img-link': {
      width: '100%',
      '& .MuiCard-root': {
        width: '100%',
      },
    },

    '& img': {
      maxWidth: '100%',
      objectFit: 'contain',
      margin: 'auto',
    },
  },
  '&. swiper-pagination': {
    position: 'absolute',
    textAlign: 'center',
    zIndex: 10,
  },
  '& .swiper-pagination-clickable': {
    position: 'absolute',
    textAlign: 'center',
    zIndex: 10,

    '& .swiper-pagination-bullet': {
      width: '12px',
      height: '12px',
      display: 'inline-block',
      borderRadius: '50%',
      margin: '0 4px',
      lineHeight: '12px',
      color: 'black',
      background: 'gray',
    },

    '& .swiper-pagination-bullet-active': {
      opacity: 1,
      color: 'white',
      background: theme.palette.primary.main,
    },
    '& .swiper-pagination-bullets': {
      '&.swiper-pagination-horizontal': {
        bottom: '8px',
        top: 'auto',
        left: 0,
        width: '100%',
      },
    },
  },
}));
export const DynamicComponentSlider = withSectionLayout(_DynamicComponentSlider);
